
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { 
  checkExistingOperator, 
  createOperatorProfile, 
  verifyOperatorAccess, 
  handleExistingUserSignup 
} from '@/services/operatorAuthService'

export const useOperatorAuth = () => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleLogin = async (email: string, password: string) => {
    setLoading(true)
    try {
      console.log('Attempting login for:', email)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      console.log('Login result:', { data, error })

      if (error) throw error

      // Check if user is an operator
      const { operator, operatorError } = await verifyOperatorAccess(data.user.id)

      if (operatorError || !operator) {
        await supabase.auth.signOut()
        throw new Error('Access denied. Account not found in operator system.')
      }

      toast({
        title: "Welcome back!",
        description: "You have been successfully signed in.",
      })
      navigate('/operator/dashboard')
    } catch (error: any) {
      console.error('Login error:', error)
      toast({
        title: "Sign in failed",
        description: error.message || 'An unexpected error occurred',
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string, 
    selectedSchool: string
  ) => {
    setLoading(true)
    try {
      console.log('Attempting signup for:', email, 'with school:', selectedSchool)
      
      if (!selectedSchool) {
        throw new Error('Please select your school')
      }

      // Check if operator already exists
      const { existingOperator } = await checkExistingOperator(email)
      if (existingOperator) {
        throw new Error('An operator account with this email already exists. Please try signing in instead.')
      }

      // Attempt to create the user account
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            school_id: selectedSchool,
            is_operator: true,
          }
        }
      })

      console.log('Signup result:', { data, error, user: data?.user, session: data?.session })

      if (error) {
        console.error('Signup error:', error)
        
        // Handle specific error cases
        if (error.message.includes('User already registered')) {
          const result = await handleExistingUserSignup(email, password, firstName, lastName, selectedSchool)
          toast({
            title: "Account activated!",
            description: result.message,
          })
          navigate('/operator/dashboard')
          return
        } else {
          throw error
        }
      }

      if (data.user && data.session) {
        console.log('New user created and session established, creating operator profile...')
        
        // Create operator profile immediately
        const { operatorData, operatorError } = await createOperatorProfile(
          data.user.id,
          email,
          firstName,
          lastName,
          selectedSchool
        )

        if (operatorError) {
          console.error('Failed to create operator profile:', operatorError)
          // Clean up the auth user if operator creation fails
          await supabase.auth.signOut()
          throw new Error(`Failed to create operator profile: ${operatorError.message}`)
        }

        console.log('Operator profile created successfully:', operatorData)
        toast({
          title: "Account created successfully!",
          description: "Your operator account has been created and you are now signed in.",
        })
        navigate('/operator/dashboard')
      } else if (data.user && !data.session) {
        // User was created but needs email confirmation
        console.log('User created but needs email confirmation')
        toast({
          title: "Check your email",
          description: "Please check your email and click the confirmation link to activate your operator account.",
        })
      } else {
        throw new Error('Failed to create user account')
      }
    } catch (error: any) {
      console.error('Signup error:', error)
      
      toast({
        title: "Sign up failed",
        description: error.message || 'An unexpected error occurred',
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    handleLogin,
    handleSignup
  }
}
